import { useState } from 'react';
import { Button } from 'reactstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { convertWinToIANA } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../Common';
import { Modal } from '../common/Modal';
import { Textarea, toasty } from '../common/forms/FormElements';
import FlagDismissalModalBody from '../timesheetManagement/FlagDismissalModalBody';

// FlagCategory.Id for "Unassigned Equipment Arrival" — these are collapsed into a
// single grouped row since one timesheet can accrue many of them at once.
const UNASSIGNED_EQUIPMENT_ARRIVAL_CATEGORY_ID = 8;

const formatAlertedOn = (utcIso, tz) => {
    if (!utcIso) return '';
    const iana = tz ? convertWinToIANA(tz) || tz : null;
    const m = iana ? moment.utc(utcIso).tz(iana) : moment.utc(utcIso).local();
    return m.format('MMM D, YYYY h:mm A z');
};

const formatDismissedBy = (flag) => (flag.isDismissed
    ? `${flag.dismissedByName || 'unknown'}${flag.dismissedAt ? ` on ${moment.utc(flag.dismissedAt).local().format('MMM D, YYYY h:mm A')}` : ''}`
    : '');

const FlagRow = ({ flag, onDismiss }) => (
    <tr>
        <td>{flag.flagType}</td>
        <td>{flag.category}</td>
        <td>{flag.employeeName || flag.equipmentName || ''}</td>
        <td>{formatAlertedOn(flag.jobStartUtc, flag.timeZone)}</td>
        <td>{formatAlertedOn(flag.alertedOnUtc, flag.timeZone)}</td>
        <td>{flag.createdByName || ''}</td>
        <td>{formatDismissedBy(flag)}</td>
        <td>{flag.dismissalNotes || ''}</td>
        <td
            className="text-right"
            style={{ width: '1%', whiteSpace: 'nowrap' }}
        >
            {!flag.isDismissed && (
                <Button
                    color="link"
                    size="sm"
                    className="text-danger p-0"
                    onClick={() => onDismiss(flag.id)}
                >
                    <FontAwesomeIcon
                        icon={faTimes}
                        className="mr-1"
                    />
                    Dismiss
                </Button>
            )}
        </td>
    </tr>
);

// Collapses every "Unassigned Equipment Arrival" flag on the timesheet into one
// row. The subject shows the first equipment with "(+N more)"; expanding lists
// them all. Dismiss clears every still-active flag in the group in one call.
const UnassignedFlagGroupRow = ({ flags, onDismissGroup }) => {
    const [expanded, setExpanded] = useState(false);

    const [first] = flags;
    const activeFlags = flags.filter((f) => !f.isDismissed);
    const allDismissed = activeFlags.length === 0;
    const dismissedRep = flags.find((f) => f.isDismissed);

    const earliestAlerted = [...flags]
        .filter((f) => f.alertedOnUtc)
        .sort((a, b) => moment.utc(a.alertedOnUtc).valueOf() - moment.utc(b.alertedOnUtc).valueOf())[0]
        ?.alertedOnUtc || first.alertedOnUtc;

    const primaryDescription = first.equipmentName || '(no description)';
    const extraCount = flags.length - 1;
    const subject = extraCount > 0 ? `${primaryDescription} (+${extraCount} more)` : primaryDescription;

    return (
        <>
            <tr>
                <td>{first.flagType}</td>
                <td>{first.category}</td>
                <td>
                    {flags.length > 1 && (
                        <Button
                            color="link"
                            size="sm"
                            className="p-0 mr-2"
                            onClick={() => setExpanded((e) => !e)}
                        >
                            <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                        </Button>
                    )}
                    {subject}
                </td>
                <td>{formatAlertedOn(first.jobStartUtc, first.timeZone)}</td>
                <td>{formatAlertedOn(earliestAlerted, first.timeZone)}</td>
                <td>{first.createdByName || ''}</td>
                <td>{allDismissed && dismissedRep ? formatDismissedBy(dismissedRep) : ''}</td>
                <td>{allDismissed && dismissedRep ? (dismissedRep.dismissalNotes || '') : ''}</td>
                <td
                    className="text-right"
                    style={{ width: '1%', whiteSpace: 'nowrap' }}
                >
                    {!allDismissed && (
                        <Button
                            color="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => onDismissGroup(activeFlags.map((f) => f.id))}
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="mr-1"
                            />
                            Dismiss
                        </Button>
                    )}
                </td>
            </tr>
            {expanded && (
                <tr>
                    <td
                        colSpan={9}
                        className="bg-light"
                    >
                        <ul className="list-unstyled mb-0 small">
                            {flags.map((f) => (
                                <li key={f.id}>
                                    {f.equipmentName || '(no description)'}
                                    {f.isDismissed && <span className="text-muted ml-1">(dismissed)</span>}
                                </li>
                            ))}
                        </ul>
                    </td>
                </tr>
            )}
        </>
    );
};

const FlagTable = ({ rowItems, onDismiss, onDismissGroup }) => (
    <table className="table table-sm table-bordered table-striped">
        <thead className="text-muted">
            <tr>
                <th>Type</th>
                <th>Category</th>
                <th>Subject</th>
                <th>Job Start</th>
                <th>Alerted</th>
                <th>Created By</th>
                <th>Dismissed By</th>
                <th>Dismissal Notes</th>
                <th />
            </tr>
        </thead>
        <tbody>
            {rowItems.map((item) => (item.kind === 'group' ? (
                <UnassignedFlagGroupRow
                    key={item.key}
                    flags={item.flags}
                    onDismissGroup={onDismissGroup}
                />
            ) : (
                <FlagRow
                    key={item.key}
                    flag={item.flag}
                    onDismiss={onDismiss}
                />
            )))}
        </tbody>
    </table>
);

const TimesheetFlagsSection = ({ timesheetId }) => {
    const queryClient = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ['timesheetFlags', timesheetId],
        queryFn: () => httpClient.js(ApiRoutes.timesheetManagement.flags(timesheetId)),
        enabled: !!timesheetId,
    });

    const dismissMutation = useMutation({
        mutationFn: ({ flagIds, notes }) => httpClient.post(
            ApiRoutes.timesheetManagement.dismissFlag(),
            { flagIds, timesheetId, notes: notes || null },
        ),
        onSuccess: () => {
            toasty.success('Flag dismissed.');
            queryClient.invalidateQueries({ queryKey: ['timesheetFlags', timesheetId] });
        },
    });

    const openDismissModal = (flagId) => {
        Modal.show({
            title: 'Timesheet Flag',
            onSubmit: async (values) => {
                const flagIdNum = Number(values?.flagId);
                if (!flagIdNum) {
                    toasty.error('No flag selected to dismiss.');
                    return;
                }
                await dismissMutation.mutateAsync({ flagIds: [flagIdNum], notes: values.notes });
            },
            SaveButtonProps: {
                label: 'Dismiss Flag',
                savingLabel: 'Dismissing',
                color: 'danger',
            },
            body: (props) => (
                <FlagDismissalModalBody
                    timesheetId={timesheetId}
                    initialFlagId={flagId}
                    {...props}
                />
            ),
        });
    };

    // Grouped unassigned-equipment dismissal: one reason, one call, all flag IDs.
    const openGroupDismissModal = (flagIds) => {
        if (!flagIds.length) {
            toasty.error('No flags to dismiss.');
            return;
        }
        Modal.show({
            title: 'Dismiss Unassigned Equipment Flags',
            initialValues: { notes: '' },
            onSubmit: async (values) => {
                await dismissMutation.mutateAsync({ flagIds, notes: values.notes });
            },
            SaveButtonProps: {
                label: 'Dismiss Flags',
                savingLabel: 'Dismissing',
                color: 'danger',
            },
            body: () => (
                <>
                    <p>
                        {`Dismissing ${flagIds.length} unassigned equipment arrival ${flagIds.length === 1 ? 'flag' : 'flags'} with one reason.`}
                    </p>
                    <Textarea
                        label="Dismissal notes (optional)"
                        id="notes"
                        name="notes"
                        placeholder="Why are these flags being dismissed?"
                        maxLength="500"
                        rows="4"
                    />
                </>
            ),
        });
    };

    if (isLoading) return <p className="text-muted mt-2">Loading flags…</p>;

    const flags = Array.isArray(data) ? data : [];
    if (flags.length === 0) return <p className="text-muted mt-2">No flags on this timesheet.</p>;

    const unassigned = flags.filter((f) => f.flagCategoryId === UNASSIGNED_EQUIPMENT_ARRIVAL_CATEGORY_ID);
    const others = flags.filter((f) => f.flagCategoryId !== UNASSIGNED_EQUIPMENT_ARRIVAL_CATEGORY_ID);

    const rowItems = others.map((f) => ({
        kind: 'single',
        flag: f,
        dismissed: f.isDismissed,
        key: `flag-${f.id}`,
    }));

    if (unassigned.length > 0) {
        rowItems.push({
            kind: 'group',
            flags: unassigned,
            dismissed: unassigned.every((f) => f.isDismissed),
            key: 'unassigned-equipment-group',
        });
    }

    // Active flags first, dismissed last — matching the prior per-row sort.
    rowItems.sort((a, b) => Number(a.dismissed) - Number(b.dismissed));

    return (
        <FlagTable
            rowItems={rowItems}
            onDismiss={openDismissModal}
            onDismissGroup={openGroupDismissModal}
        />
    );
};

export default TimesheetFlagsSection;
